import { createFileRoute } from '@tanstack/react-router'
import { useMenuItems, useEvent, useMenuItem, useUpdateMenuItem, useCreateMenuItem, useDeleteMenuItem, useVendorEvents, useVendorGatingExemption, useCanAddMenuItems } from '@/lib/api/queries'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, Plus, Package, Pencil, X, Calendar, Check, Trash2, CreditCard } from 'lucide-react'
import { useState, useMemo } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value'
import type { MenuItem, VendorEvent } from '@/lib/api/types'
import { useItemDiscounts } from '@/lib/api/discount-queries'
import { ActiveDiscountBadge } from '@/features/promotions/active-discount-badge'
import { hasDiscountedPrice } from '@/features/promotions/menu-price'
import { ItemDiscountsPanel } from '@/features/promotions/item-discounts-panel'

export const Route = createFileRoute('/_authenticated/events/$eventId/menu')({
  component: MenuPage,
})

// Helper to get vendor info from menu item
function getVendorInfo(item: MenuItem): string {
  if (typeof item.vendor_event === 'object' && item.vendor_event) {
    const vendor = item.vendor_event.vendor
    const vendorName = typeof vendor === 'object' && vendor ? vendor.name : ''
    const booth = item.vendor_event.booth || ''
    if (vendorName && booth && vendorName !== booth) {
      return `${vendorName} - ${booth}`
    }
    return vendorName || booth || ''
  }
  return ''
}

// Route a DRF 400 body ({price: ["..."]}, {discount_price: ["..."]}) to the
// inline field errors; anything else (or extra fields) keeps the submit banner.
function getMenuItemSaveErrors(err: unknown, fallback: string): Record<string, string> {
  const data = (err as { data?: Record<string, unknown> } | null)?.data ?? {}
  const first = (v: unknown) => { const s = Array.isArray(v) ? v[0] : v; return typeof s === 'string' ? s : undefined }
  const errors: Record<string, string> = {}
  for (const field of ['price', 'discount_price']) { const msg = first(data[field]); if (msg) errors[field] = msg }
  if (Object.keys(errors).length && Object.keys(errors).length === Object.keys(data).length) return errors
  return { ...errors, submit: first(data.detail) || first(data.non_field_errors) || (err instanceof Error ? err.message : fallback) }
}

function MenuPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  
  // Permission checks
  const canViewMenu = usePermission('can_view_menu')
  const canCreateMenu = useCanAddMenuItems(eventIdNum)
  const canEditMenu = usePermission('can_edit_menu')
  
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 400)
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 100
  
  // Permission guard
  if (!canViewMenu) {
    return <AccessDenied message="You don't have permission to view the menu." />
  }
  
  const { data: event } = useEvent(eventIdNum)
  const { data, isLoading, error } = useMenuItems({
    vendor_event__event: eventIdNum,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    search: debouncedSearch || undefined,
  })

  const tokenValue = event?.token_value || 2.0
  const eventType = event?.event_type || 'token'

  const items = data?.results || []
  const totalCount = data?.count || 0
  const totalPages = Math.ceil(totalCount / pageSize)

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto container:px-8 px-4">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Header */}
        <div className="flex flex-row justify-between items-center mb-3 sm:mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">Menu</h1>
          {canCreateMenu && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Item
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="space-y-6">
          <div className="flex flex-row gap-4 items-start sm:items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 dark:text-neutral-400" />
              <Input
                type="text"
                placeholder="Search by Item, Type, Vendor or Dietary Restriction..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-10 bg-white dark:bg-[#131313]"
              />
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="overflow-x-auto">
              <div className="overflow-scroll border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                  <thead className="bg-neutral-50 dark:bg-neutral-950">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Item Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Tokens</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-6 py-4 whitespace-normal text-sm font-medium">
                          <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-40" />
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm">
                          <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-24" />
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm">
                          <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-16" />
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm">
                          <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-64" />
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm">
                          <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-20" />
                        </td>
                        <td className="px-6 py-4 whitespace-normal text-sm">
                          <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-16" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : error ? (
            <div className="p-6 border border-red-200 dark:border-red-900 rounded-lg">
              <p className="text-red-600 dark:text-red-400">
                Failed to load menu items: {error.message}
              </p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center border border-neutral-200 dark:border-neutral-800 rounded-lg">
              <Package className="w-12 h-12 mx-auto text-neutral-400 mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
                No menu items found
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                {search ? 'Try a different search term.' : 'Add your first menu item to get started.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-rows-[1fr_auto]">
              <div className="overflow-x-auto">
                <div className="overflow-scroll border border-neutral-200 dark:border-neutral-800 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                    <thead className="bg-neutral-50 dark:bg-neutral-950 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                          Item Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                          Vendor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                          {eventType === 'direct_pay' ? 'Price' : 'Tokens'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                      {items.map((item) => (
                        <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-950">
                          {/* Item Name */}
                          <td className="px-6 py-4 max-w-64">
                            <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 break-words">
                              {item.name}
                            </div>
                          </td>
                          {/* Vendor */}
                          <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400 max-w-40">
                            <div className="break-words">
                              {getVendorInfo(item)}
                            </div>
                          </td>
                          {/* Price */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100">
                            {hasDiscountedPrice(item) ? (
                              <ActiveDiscountBadge item={item} />
                            ) : eventType === 'direct_pay' ? (
                              <span className="font-medium">${(item.price * tokenValue).toFixed(2)}</span>
                            ) : (
                              <span className="font-medium">{Math.floor(item.price)}</span>
                            )}
                          </td>
                          {/* Description */}
                          <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400 max-w-40">
                            <div className="truncate" title={item.description || ''}>
                              {item.description || 'No description'}
                            </div>
                          </td>
                          {/* Status */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              {item.is_available ? (
                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" title="Available">
                                  <Check className="h-3 w-3" />
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400">
                                  Unavailable
                                </span>
                              )}
                              {item.direct_pay && (
                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" title="Direct Pay">
                                  <CreditCard className="h-3 w-3" />
                                </span>
                              )}
                              {item.direct_pay_only_pos && (
                                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400" title="Direct pay from POS only">
                                  <CreditCard className="h-3 w-3" />
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Actions */}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {canEditMenu && (
                                <button
                                  onClick={() => setEditingItemId(item.id)}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20 hover:bg-blue-200 dark:hover:bg-blue-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                  <Pencil className="w-3 h-3 mr-1" />
                                  Edit
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalCount > 0 && (
                <div className="bg-white dark:bg-[#131313] px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-200 dark:border-neutral-800 sm:px-6">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3 sm:mb-0">
                    Showing <span className="font-medium">{items.length}</span> of <span className="font-medium">{totalCount}</span> items
                  </p>
                  {totalPages > 1 && (
                    <div className="flex items-center flex-wrap gap-1">
                      {currentPage > 1 && (
                        <button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          className="px-3 py-1 border border-neutral-200 dark:border-neutral-800 rounded-md text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                        >
                          Prev
                        </button>
                      )}
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                        <button
                          key={num}
                          onClick={() => setCurrentPage(num)}
                          className={`px-3 py-1 rounded-md text-sm ${
                            currentPage === num
                              ? 'bg-blue-600 text-white'
                              : 'border border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                      {currentPage < totalPages && (
                        <button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          className="px-3 py-1 border border-neutral-200 dark:border-neutral-800 rounded-md text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                        >
                          Next
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Menu Item Modal */}
      {editingItemId && event && (
        <EditMenuItemModal
          itemId={editingItemId}
          event={event}
          tokenValue={tokenValue}
          eventType={eventType}
          onClose={() => setEditingItemId(null)}
        />
      )}

      {/* Create Menu Item Modal */}
      {showCreateModal && (
        <CreateMenuItemModal
          eventId={eventIdNum}
          tokenValue={tokenValue}
          eventType={eventType}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  )
}

// Parse a flat (currency) fee-override input. Empty string => null (inherit
// from the event); any non-negative number (including 0, an explicit zero)
// is kept. NaN / negative falls back to null so a malformed field never
// sends garbage to the backend. Currency — NOT scaled.
function parseFeeOverride(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const n = parseFloat(trimmed)
  return Number.isFinite(n) && n >= 0 ? n : null
}

// IMPORTANT — percentage scale. The backend's direct-pay math stores
// `tax_percentage` / `additional_fee_percentage` as FRACTIONS (0.13 = 13%);
// `common/direct_pay_tax_fee.py` multiplies WITHOUT dividing by 100
// (unlike variable/platform fees, which are percents). So the studio shows
// operators a PERCENT and converts on the boundary — exactly how
// settings.tsx handles event-level tax/fee. Type 13 → store 0.13.
const roundPct = (n: number) => Math.round(n * 1e6) / 1e6 // trims float noise
// fraction (0.13) -> percent input string ("13")
function feePctToInput(fraction: number | null | undefined): string {
  if (fraction == null) return ''
  return String(roundPct(fraction * 100))
}
// percent input string ("13") -> fraction (0.13), or null when blank
function parseFeePctToFraction(percentInput: string): number | null {
  const trimmed = percentInput.trim()
  if (trimmed === '') return null
  const n = parseFloat(trimmed)
  return Number.isFinite(n) && n >= 0 ? roundPct(n / 100) : null
}

interface DirectPayFeeInputsProps {
  values: {
    tax_percentage: string
    additional_fee_percentage: string
    additional_fee_fixed: string
  }
  onChange: (
    field: 'tax_percentage' | 'additional_fee_percentage' | 'additional_fee_fixed',
    value: string,
  ) => void
  disabled?: boolean
  /** Event-level defaults, shown as placeholders so the operator knows what
   *  a blank field inherits. */
  eventTaxPercentage?: number
  eventAdditionalFeePercentage?: number
  currency?: string
}

// Per-item direct-pay fee overrides, shared by the edit + create modals.
// Only rendered when direct_pay is on. Each value is a form string: '' means
// "inherit from the event", a number (incl. 0) is an explicit override.
// All three REPLACE the corresponding event fee for this item (they do NOT
// stack on top of it). The two additional_fee_* fields are mutually
// exclusive (enforced here + by a backend CheckConstraint).
function DirectPayFeeInputs({
  values,
  onChange,
  disabled,
  eventTaxPercentage,
  eventAdditionalFeePercentage,
  currency = '',
}: DirectPayFeeInputsProps) {
  const pctSet = values.additional_fee_percentage.trim() !== ''
  const fixedSet = values.additional_fee_fixed.trim() !== ''
  const inputCls =
    'w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
  // Event-level defaults are FRACTIONS (0.13); show operators a percent.
  const taxPlaceholder =
    eventTaxPercentage != null ? `Inherits ${roundPct(eventTaxPercentage * 100)}%` : 'Inherit'
  const addPctPlaceholder = fixedSet
    ? 'Disabled — flat fee is set'
    : eventAdditionalFeePercentage != null
      ? `Replaces ${roundPct(eventAdditionalFeePercentage * 100)}%`
      : 'Inherit'

  return (
    <div className="mt-4 rounded-lg border border-neutral-200 dark:border-neutral-800 p-3 space-y-3">
      <p className="text-xs font-semibold text-neutral-700 dark:text-neutral-300">
        Direct Pay Fees{' '}
        <span className="font-normal text-neutral-500 dark:text-neutral-400">
          — leave blank to inherit the event defaults
        </span>
      </p>

      {/* Tax on its own row. */}
      <div>
        <label className="block text-xs text-neutral-600 dark:text-neutral-400 mb-1">
          Tax % (override)
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={values.tax_percentage}
          onChange={(e) => onChange('tax_percentage', e.target.value)}
          disabled={disabled}
          placeholder={taxPlaceholder}
          className={inputCls}
        />
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
          Replaces the event tax rate for this item.
        </p>
      </div>

      {/* Additional fee % + flat fee side-by-side on desktop, stacked on mobile. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-neutral-600 dark:text-neutral-400 mb-1">
            Additional fee % (replaces event)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={values.additional_fee_percentage}
            onChange={(e) => onChange('additional_fee_percentage', e.target.value)}
            disabled={disabled || fixedSet}
            placeholder={addPctPlaceholder}
            className={inputCls}
          />
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
            Replaces the event additional fee % for this item (doesn&apos;t stack).
          </p>
        </div>

        <div>
          <label className="block text-xs text-neutral-600 dark:text-neutral-400 mb-1">
            Additional flat fee {currency ? `(${currency})` : ''}
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={values.additional_fee_fixed}
            onChange={(e) => onChange('additional_fee_fixed', e.target.value)}
            disabled={disabled || pctSet}
            placeholder={pctSet ? 'Disabled — % fee is set' : 'Inherit'}
            className={inputCls}
          />
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
            Flat fee per item. Mutually exclusive with the % fee.
          </p>
        </div>
      </div>
    </div>
  )
}

// Edit Menu Item Modal Component
export interface EditMenuItemModalProps {
  itemId: number
  event: import('@/lib/api/types').Event
  tokenValue: number
  eventType: string
  onClose: () => void
}

export function EditMenuItemModal({ itemId, event, tokenValue, eventType, onClose }: EditMenuItemModalProps) {
  const { data: item, isLoading } = useMenuItem(itemId)
  const updateMutation = useUpdateMenuItem()
  const deleteMutation = useDeleteMenuItem()

  const isActuallyVendorUser = usePermission('is_vendor')
  const isExemptFromVendorGating = useVendorGatingExemption(event)
  const hasDeletePermission = usePermission('delete_vendoreventitem')
  // Promotions targeting the item: shown to discount viewers while the
  // event's promotions are on, or when something still targets the item.
  const canViewDiscounts = usePermission('can_view_discounts')
  const { data: itemDiscounts } = useItemDiscounts(canViewDiscounts ? itemId : 0)
  const showPromotions = canViewDiscounts && (!!event.enable_discount_schedules || !!itemDiscounts?.schedules.length)

  // The vendor_can_modify_item_* flags only restrict users acting purely as
  // vendor admins for this event, mirroring the backend exemption
  // (VendorEventItemViewSet._is_vendor_level_admin): superusers, event admins
  // for this event, and org admins for its organization are never gated even
  // if their global permission union carries is_vendor (dual-role users).
  // Fails closed: a vendor-flagged user stays gated until a probe confirms.
  const vendorGated = isActuallyVendorUser && !isExemptFromVendorGating
  const canEditPrice = vendorGated ? (event.vendor_can_modify_item_price !== false) : true
  const canEditDetails = vendorGated ? (event.vendor_can_modify_item_details !== false) : true
  const canEditAvailability = vendorGated ? (event.vendor_can_modify_item_availability !== false) : true
  const canDeleteItem = hasDeletePermission && (!vendorGated || event.vendor_can_create_items !== false)
  
  const [activeTab, setActiveTab] = useState<'details' | 'options'>('details')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discount_price: '',
    discounted: false,
    is_available: true,
    hide_from_menu: false,
    is_vegetarian: false,
    is_vegan: false,
    is_dairy_free: false,
    is_gluten_free: false,
    is_beverage: false,
    is_alcoholic: false,
    direct_pay: false,
    direct_pay_only_pos: false,
    // Direct-pay fee overrides as form strings; '' = inherit from event.
    tax_percentage: '',
    additional_fee_percentage: '',
    additional_fee_fixed: '',
    max_qty_per_order: '0',
    max_qty_per_user: '0',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isInitialized, setIsInitialized] = useState(false)
  // Whether the form holds the item's stored sale price, as the form shows it.
  // Only a sale the user changes is checked and sent: a stored one the form
  // wouldn't accept (a fraction, set through the API or a CSV import) is left
  // as it is, so it doesn't block saving the item's other fields.
  const isStoredSale = (discounted: boolean, discountPrice: string) =>
    discounted === !!item?.discount_price && (!discounted || discountPrice === String(item?.discount_price || 0))

  // null/undefined fee override => '' (inherit); a number (incl. 0) => its string.
  const feeToInput = (v: number | null | undefined) => (v == null ? '' : String(v))

  // Initialize form data when item loads
  useMemo(() => {
    if (item && !isInitialized) {
      setFormData({
        name: item.name || '',
        description: item.description_en || item.description || '',
        price: String(item.price || 0),
        discount_price: String(item.discount_price || 0),
        discounted: !!item.discount_price,
        is_available: item.is_available ?? true,
        hide_from_menu: item.hide_from_menu ?? false,
        is_vegetarian: item.is_vegetarian ?? false,
        is_vegan: item.is_vegan ?? false,
        is_dairy_free: item.is_dairyfree ?? false,
        is_gluten_free: item.is_glutenfree ?? false,
        is_beverage: item.is_beverage ?? false,
        is_alcoholic: item.is_alcoholic ?? false,
        direct_pay: item.direct_pay ?? false,
        direct_pay_only_pos: item.direct_pay_only_pos ?? false,
        // Percentages: backend stores fractions, show operators a percent.
        tax_percentage: feePctToInput(item.tax_percentage),
        additional_fee_percentage: feePctToInput(item.additional_fee_percentage),
        // Flat fee is currency — shown as-is.
        additional_fee_fixed: feeToInput(item.additional_fee_fixed),
        max_qty_per_order: String(item.max_qty_per_order || 0),
        max_qty_per_user: String(item.max_qty_per_user || 0),
      })
      setIsInitialized(true)
    }
  }, [item, isInitialized])

  // Update a fee-override field, enforcing the percentage/flat mutual
  // exclusivity by clearing the counterpart when one is given a value.
  const handleFeeChange = (
    field: 'tax_percentage' | 'additional_fee_percentage' | 'additional_fee_fixed',
    value: string,
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'additional_fee_percentage' && value.trim() !== '') next.additional_fee_fixed = ''
      if (field === 'additional_fee_fixed' && value.trim() !== '') next.additional_fee_percentage = ''
      return next
    })
  }

  const vendorName = useMemo(() => {
    if (typeof item?.vendor_event === 'object' && item?.vendor_event) {
      const vendor = item.vendor_event.vendor
      return (typeof vendor === 'object' && vendor ? vendor.name : '') || 'Vendor'
    }
    return 'Vendor'
  }, [item])

  const vendorEventId = useMemo(() => {
    if (typeof item?.vendor_event === 'object' && item?.vendor_event) {
      return item.vendor_event.id
    }
    return item?.vendor_event
  }, [item])

  const validateField = (field: string, value: string) => {
    const newErrors = { ...errors }
    
    if (field === 'name' && !value.trim()) {
      newErrors.name = 'Item name is required.'
    } else if (field === 'name') {
      delete newErrors.name
    }
    
    if (field === 'price') {
      const priceVal = parseInt(value)
      if (!value || isNaN(priceVal) || priceVal < 0 || priceVal !== parseFloat(value)) {
        newErrors.price = 'Please enter a valid whole number for tokens.'
      } else {
        delete newErrors.price
      }
    }
    
    if (field === 'discount_price' && formData.discounted && !isStoredSale(true, value)) {
      const discountVal = parseInt(value)
      if (!value || isNaN(discountVal) || discountVal < 0 || discountVal !== parseFloat(value)) {
        newErrors.discount_price = 'Please enter a valid whole number for discount tokens.'
      } else {
        delete newErrors.discount_price
      }
    } else if (field === 'discount_price') {
      delete newErrors.discount_price
    }
    
    setErrors(newErrors)
  }

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${formData.name}"? This action cannot be undone.`)) return
    try {
      await deleteMutation.mutateAsync(itemId)
      onClose()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete item'
      setErrors({ submit: errorMessage })
    }
  }

  const handleSubmit = async () => {
    // Validate
    const newErrors: Record<string, string> = {}
    if (canEditDetails && !formData.name.trim()) newErrors.name = 'Item name is required'
    const saleUnchanged = isStoredSale(formData.discounted, formData.discount_price)

    // Only validate price fields if user can edit them
    if (canEditPrice) {
      const priceVal = parseInt(formData.price)
      if (!formData.price || isNaN(priceVal) || priceVal < 0 || priceVal !== parseFloat(formData.price)) {
        newErrors.price = 'Valid whole number is required'
      }
      if (formData.discounted && !saleUnchanged) {
        const discountVal = parseInt(formData.discount_price)
        if (!formData.discount_price || isNaN(discountVal) || discountVal < 0 || discountVal !== parseFloat(formData.discount_price)) {
          newErrors.discount_price = 'Valid whole number is required when discount is enabled'
        } else if (discountVal === 0) {
          // The backend reads 0 as no discount, so saving it would quietly drop the sale.
          newErrors.discount_price = 'Discount price must be more than 0'
        } else if (!newErrors.price && discountVal >= priceVal) {
          newErrors.discount_price = 'Discount price must be less than the price'
        }
      } else if (formData.discounted && !newErrors.price && parseFloat(formData.discount_price) >= priceVal) {
        // The stored sale still has to stay below a new price.
        newErrors.discount_price = 'Discount price must be less than the price'
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      if (newErrors.price || newErrors.discount_price) setActiveTab('details')
      return
    }

    try {
      // Build update data based on what the user has permission to edit
      const updateData: Partial<MenuItem> = {
        vendor_event: vendorEventId,
      }
      
      // Only include detail fields if user can edit them
      if (canEditDetails) {
        updateData.name = formData.name
        updateData.description = formData.description
        updateData.is_vegetarian = formData.is_vegetarian
        updateData.is_vegan = formData.is_vegan
        updateData.is_dairyfree = formData.is_dairy_free
        updateData.is_glutenfree = formData.is_gluten_free
        updateData.is_beverage = formData.is_beverage
        updateData.is_alcoholic = formData.is_alcoholic
      }
      
      // Only include price fields if user can edit them
      if (canEditPrice) {
        updateData.price = parseInt(formData.price)
        // Unchecked sends 0, the backend's "no discount" (the field isn't nullable).
        // An unchanged sale isn't sent, so a stored fraction isn't rounded.
        if (!saleUnchanged) updateData.discount_price = formData.discounted ? parseInt(formData.discount_price) : 0
        updateData.direct_pay = formData.direct_pay
        updateData.direct_pay_only_pos = formData.direct_pay_only_pos
        // Fee overrides: send null for blank (inherit), number otherwise.
        // Percentages convert percent→fraction (13 → 0.13); flat fee is
        // currency, sent as-is. When direct_pay is OFF, force all three to
        // null — fee overrides only apply to direct-pay items, and persisting
        // them while disabled would let them re-apply if it's re-enabled.
        updateData.tax_percentage = formData.direct_pay ? parseFeePctToFraction(formData.tax_percentage) : null
        updateData.additional_fee_percentage = formData.direct_pay ? parseFeePctToFraction(formData.additional_fee_percentage) : null
        updateData.additional_fee_fixed = formData.direct_pay ? parseFeeOverride(formData.additional_fee_fixed) : null
        updateData.max_qty_per_order = parseInt(formData.max_qty_per_order) || 0
        updateData.max_qty_per_user = parseInt(formData.max_qty_per_user) || 0
      }
      
      // Only include availability fields if user can edit them
      if (canEditAvailability) {
        updateData.is_available = formData.is_available
        updateData.hide_from_menu = formData.hide_from_menu
      }
      
      await updateMutation.mutateAsync({
        id: itemId,
        data: updateData,
      })
      onClose()
    } catch (err) {
      console.error('Error updating menu item:', err)
      const saveErrors = getMenuItemSaveErrors(err, 'Failed to update menu item')
      setErrors(saveErrors)
      if (saveErrors.price || saveErrors.discount_price) setActiveTab('details')
    }
  }

  if (isLoading || !item) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-[#131313] rounded-lg p-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-[#131313] rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col sm:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Item Preview (hidden on mobile, shown at top on mobile via order) */}
        <div 
          className="hidden sm:flex w-80 p-6 flex-col flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}
        >
          <div className="text-center text-white mb-6">
            <h3 className="text-2xl font-bold">{formData.name || 'Menu Item'}</h3>
            <div className="flex items-center justify-center mt-2">
              <Calendar className="w-5 h-5 mr-2" />
              <span className="text-blue-100">{vendorName}</span>
            </div>
          </div>
          
          {/* Item Preview Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
            <div className="mb-3">
              <span className="text-blue-100 text-sm font-semibold">Description</span>
              <p className="text-white text-sm font-medium">{formData.description || 'No description'}</p>
            </div>
            
            <div className="mb-3">
              <span className="text-blue-100 text-sm font-semibold">Price</span>
              {eventType === 'direct_pay' ? (
                <p className="text-white font-semibold whitespace-nowrap">
                  ${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)}
                </p>
              ) : (
                <p className="text-white font-semibold whitespace-nowrap">
                  {formData.price || '0'} tokens{' '}
                  <span className="text-blue-200 text-sm font-normal">
                    (${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)})
                  </span>
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.is_available ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm font-semibold">{formData.is_available ? 'Available' : 'Unavailable'}</span>
            </div>
          </div>
          
          {/* Action Buttons - Desktop */}
          <div className="mt-auto pt-6 space-y-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={updateMutation.isPending}
              className="w-full bg-white text-blue-600 font-semibold py-3 px-4 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <div className="flex gap-2">
              {canDeleteItem && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="w-[20%] flex items-center justify-center bg-transparent border border-white/30 text-white/70 py-3 rounded-md hover:bg-red-500/20 hover:text-white hover:border-red-300/40 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className={`${canDeleteItem ? 'w-[80%]' : 'w-full'} bg-transparent border border-white/30 text-white font-semibold py-3 px-4 rounded-md hover:bg-white/10 transition-colors`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel - Form Content */}
        <div className="flex-1 flex flex-col max-h-[95vh] sm:max-h-[90vh] min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200 dark:border-neutral-800">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 dark:text-neutral-100 truncate">{formData.name || 'Edit Menu Item'}</h2>
              <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">{vendorName}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors ml-2 flex-shrink-0">
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-neutral-200 dark:border-neutral-800 px-6">
            <nav className="flex space-x-8">
                <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-2 inline">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M16 8v5a3 3 0 0 0 6 0v-5a4 4 0 1 0-8 0Z" />
                  <line x1="2" y1="9" x2="2" y2="20" />
                  <line x1="4" y1="9" x2="4" y2="20" />
                </svg>
                Item Details
              </button>
              
              <button
                onClick={() => setActiveTab('options')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-sm ${
                  activeTab === 'options'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-2 inline">
                  <path d="M3 6h18l-2 13H5L3 6Z" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Options & Dietary
              </button>
            </nav>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto">
            {errors.submit && (
              <div className="m-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
              </div>
            )}

            {/* Item Details Tab */}
            {activeTab === 'details' && (
              <div className="p-6 space-y-6">
                {/* Item Name */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                    Item Name*
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, name: e.target.value }))
                      validateField('name', e.target.value)
                    }}
                    disabled={!canEditDetails}
                    placeholder="Enter item name"
                    className={`w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                      canEditDetails ? 'bg-white dark:bg-[#131313]' : ''
                    } ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  {!canEditDetails && (
                    <p className="text-xs text-neutral-500 mt-1">You don't have permission to modify item details</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={!canEditDetails}
                    rows={4}
                    placeholder="Item description"
                    className={`w-full text-sm px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed ${
                      canEditDetails ? 'bg-white dark:bg-[#131313]' : ''
                    }`}
                  />
                </div>

                {/* Pricing Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Token Price */}
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                      {eventType === 'direct_pay' ? 'Price*' : 'Token Price*'}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, price: e.target.value }))
                          validateField('price', e.target.value)
                        }}
                        disabled={!canEditPrice}
                        step="1"
                        min="0"
                        placeholder="0"
                        className={`w-full px-3 py-2 pr-36 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:text-neutral-400 disabled:cursor-not-allowed ${
                          canEditPrice ? 'bg-white dark:bg-[#131313]' : ''
                        } ${errors.price ? 'border-red-500' : ''}`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {eventType === 'direct_pay' ? (
                          <span className="text-neutral-500 text-sm font-medium">
                            ${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-neutral-500 text-sm font-medium">
                            tokens <span className="text-neutral-400 ml-1">
                              (${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)})
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
                    {!canEditPrice && (
                      <p className="text-xs text-neutral-500 mt-1">You don't have permission to modify prices</p>
                    )}
                  </div>

                  {/* Discount Price */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="edit-item-discount-price" className={`block text-sm font-semibold ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                        Discount Price
                      </label>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="edit-item-discounted"
                          checked={formData.discounted}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              discounted: e.target.checked,
                              discount_price: e.target.checked ? prev.discount_price : '',
                            }))
                            if (!e.target.checked) {
                              setErrors(prev => {
                                const newErrors = { ...prev }
                                delete newErrors.discount_price
                                return newErrors
                              })
                            }
                          }}
                          disabled={!canEditPrice}
                          className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label htmlFor="edit-item-discounted" className={`ml-2 text-sm ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-600 dark:text-neutral-400'}`}>
                          Enable Discount
                        </label>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        id="edit-item-discount-price"
                        type="number"
                        value={formData.discount_price}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, discount_price: e.target.value }))
                          validateField('discount_price', e.target.value)
                        }}
                        disabled={!formData.discounted || !canEditPrice}
                        step="1"
                        min="0"
                        placeholder="0"
                        className={`w-full px-3 py-2 pr-36 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:text-neutral-400 disabled:cursor-not-allowed ${
                          errors.discount_price ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {eventType === 'direct_pay' ? (
                          <span className="text-neutral-500 text-sm font-medium">
                            ${(parseFloat(formData.discount_price || '0') * tokenValue).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-neutral-500 text-sm font-medium">
                            tokens {formData.discounted && formData.discount_price && (
                              <span className="text-neutral-400 ml-1">
                                (${(parseFloat(formData.discount_price || '0') * tokenValue).toFixed(2)})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {errors.discount_price && <p className="text-red-500 text-sm mt-1">{errors.discount_price}</p>}
                  </div>
                </div>

                {/* Quantity Limits Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Max Per Order */}
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                      Max Per Order
                    </label>
                    <input
                      type="number"
                      value={formData.max_qty_per_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_qty_per_order: e.target.value }))}
                      disabled={!canEditPrice}
                      step="1"
                      min="0"
                      placeholder="0"
                      className={`w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:text-neutral-400 disabled:cursor-not-allowed ${
                        canEditPrice ? 'bg-white dark:bg-[#131313]' : ''
                      }`}
                    />
                    <p className="text-xs text-neutral-500 mt-1">Max items per order (0 = unlimited)</p>
                  </div>

                  {/* Max Per User */}
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                      Max Per User
                    </label>
                    <input
                      type="number"
                      value={formData.max_qty_per_user}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_qty_per_user: e.target.value }))}
                      disabled={!canEditPrice}
                      step="1"
                      min="0"
                      placeholder="0"
                      className={`w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:text-neutral-400 disabled:cursor-not-allowed ${
                        canEditPrice ? 'bg-white dark:bg-[#131313]' : ''
                      }`}
                    />
                    <p className="text-xs text-neutral-500 mt-1">Max total per user (0 = unlimited)</p>
                  </div>
                </div>

                {showPromotions && <ItemDiscountsPanel itemId={itemId} />}
              </div>
            )}

            {/* Options & Dietary Tab */}
            {activeTab === 'options' && (
              <div className="p-6 space-y-6">
                {/* Availability */}
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${!canEditAvailability ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                    Item Availability
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_available}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                        disabled={!canEditAvailability}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditAvailability ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Available for ordering</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.hide_from_menu}
                        onChange={(e) => setFormData(prev => ({ ...prev, hide_from_menu: e.target.checked }))}
                        disabled={!canEditAvailability}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-orange-600 focus:ring-orange-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditAvailability ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Hide from menu display</label>
                    </div>
                  </div>
                  {!canEditAvailability && (
                    <p className="text-xs text-neutral-500 mt-2">You don't have permission to modify item availability</p>
                  )}
                </div>

                {/* Payment Options */}
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                    Payment Options
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.direct_pay}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            direct_pay: e.target.checked,
                            // Turning Direct Pay off clears the dependent fields
                            // (POS-only + the fee overrides) so we never keep
                            // hidden values that would silently re-apply if it's
                            // switched back on later.
                            direct_pay_only_pos: e.target.checked ? prev.direct_pay_only_pos : false,
                            tax_percentage: e.target.checked ? prev.tax_percentage : '',
                            additional_fee_percentage: e.target.checked ? prev.additional_fee_percentage : '',
                            additional_fee_fixed: e.target.checked ? prev.additional_fee_fixed : '',
                          }))}
                          disabled={!canEditPrice}
                          className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label className={`ml-2 text-sm ${!canEditPrice ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>
                          Direct Pay
                        </label>
                      </div>
                      <p className="text-xs text-neutral-500 mt-1 ml-6">Allow payment without tokens</p>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.direct_pay_only_pos}
                          onChange={(e) => setFormData(prev => ({ ...prev, direct_pay_only_pos: e.target.checked }))}
                          disabled={!formData.direct_pay || !canEditPrice}
                          className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label className={`ml-2 text-sm ${formData.direct_pay && canEditPrice ? 'text-neutral-700 dark:text-neutral-300' : 'text-neutral-400 dark:text-neutral-500'}`}>
                          POS Only
                        </label>
                      </div>
                      <p className={`text-xs mt-1 ml-6 ${formData.direct_pay ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        Restrict direct pay to point of sale
                      </p>
                    </div>
                  </div>
                  {formData.direct_pay && (
                    <DirectPayFeeInputs
                      values={{
                        tax_percentage: formData.tax_percentage,
                        additional_fee_percentage: formData.additional_fee_percentage,
                        additional_fee_fixed: formData.additional_fee_fixed,
                      }}
                      onChange={handleFeeChange}
                      disabled={!canEditPrice}
                      eventTaxPercentage={event?.tax_percentage}
                      eventAdditionalFeePercentage={event?.additional_fee_percentage}
                      currency={event?.currency}
                    />
                  )}
                  {!canEditPrice && (
                    <p className="text-xs text-neutral-500 mt-2">You don't have permission to modify payment options</p>
                  )}
                </div>

                {/* Dietary Options */}
                <div>
                  <label className={`block text-sm font-semibold mb-3 ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-600' : 'text-neutral-900 dark:text-neutral-100'}`}>
                    Dietary Options
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_vegetarian}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_vegetarian: e.target.checked }))}
                        disabled={!canEditDetails}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Vegetarian</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_vegan}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_vegan: e.target.checked }))}
                        disabled={!canEditDetails}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Vegan</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_dairy_free}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_dairy_free: e.target.checked }))}
                        disabled={!canEditDetails}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Dairy Free</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_gluten_free}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_gluten_free: e.target.checked }))}
                        disabled={!canEditDetails}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Gluten Free</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_beverage}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_beverage: e.target.checked }))}
                        disabled={!canEditDetails}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Beverage</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_alcoholic}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_alcoholic: e.target.checked }))}
                        disabled={!canEditDetails}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-red-600 focus:ring-red-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label className={`ml-2 text-sm ${!canEditDetails ? 'text-neutral-400 dark:text-neutral-500' : 'text-neutral-700 dark:text-neutral-300'}`}>Alcoholic</label>
                    </div>
                  </div>
                  {!canEditDetails && (
                    <p className="text-xs text-neutral-500 mt-2">You don't have permission to modify dietary options</p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Mobile Action Buttons */}
          <div className="sm:hidden p-4 border-t border-neutral-200 dark:border-neutral-800 space-y-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={updateMutation.isPending}
              className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <div className="flex gap-2">
              {canDeleteItem && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="w-[20%] flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500 py-3 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className={`${canDeleteItem ? 'w-[80%]' : 'w-full'} bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-semibold py-3 px-4 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Create Menu Item Modal Component
export interface CreateMenuItemModalProps {
  eventId: number
  tokenValue: number
  eventType: string
  onClose: () => void
  defaultVendorEvent?: VendorEvent | null
}

export function CreateMenuItemModal({ eventId, tokenValue, eventType, onClose, defaultVendorEvent }: CreateMenuItemModalProps) {
  const { data: vendorEventsData } = useVendorEvents(eventId)
  // Event is already cached by the parent route; used for fee placeholders.
  const { data: event } = useEvent(eventId)
  const createMutation = useCreateMenuItem()

  const [activeTab, setActiveTab] = useState<'details' | 'options'>('details')
  const [vendorSearch, setVendorSearch] = useState('')
  const [selectedVendorEvent, setSelectedVendorEvent] = useState<VendorEvent | null>(defaultVendorEvent || null)
  const [showVendorDropdown, setShowVendorDropdown] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discount_price: '',
    discounted: false,
    is_available: true,
    hide_from_menu: false,
    is_vegetarian: false,
    is_vegan: false,
    is_dairy_free: false,
    is_gluten_free: false,
    is_beverage: false,
    is_alcoholic: false,
    direct_pay: false,
    direct_pay_only_pos: false,
    // Direct-pay fee overrides as form strings; '' = inherit from event.
    tax_percentage: '',
    additional_fee_percentage: '',
    additional_fee_fixed: '',
    max_qty_per_order: '0',
    max_qty_per_user: '0',
  })

  // Update a fee-override field, enforcing percentage/flat mutual exclusivity.
  const handleFeeChange = (
    field: 'tax_percentage' | 'additional_fee_percentage' | 'additional_fee_fixed',
    value: string,
  ) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'additional_fee_percentage' && value.trim() !== '') next.additional_fee_fixed = ''
      if (field === 'additional_fee_fixed' && value.trim() !== '') next.additional_fee_percentage = ''
      return next
    })
  }
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Filter vendor events based on search
  const filteredVendorEvents = useMemo(() => {
    const vendorEvents = vendorEventsData?.results || []
    if (!vendorSearch) return vendorEvents
    const searchLower = vendorSearch.toLowerCase()
    return vendorEvents.filter(ve => {
      const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ''
      const booth = ve.booth || ve.name || ''
      return vendorName.toLowerCase().includes(searchLower) || booth.toLowerCase().includes(searchLower)
    })
  }, [vendorEventsData, vendorSearch])

  // Helper to get vendor event display name
  const getVendorEventDisplayName = (ve: VendorEvent): string => {
    const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ''
    const booth = ve.booth || ve.name || ''
    if (vendorName && booth && vendorName !== booth) {
      return `${vendorName} - ${booth}`
    }
    return vendorName || booth
  }

  const validateField = (field: string, value: string) => {
    const newErrors = { ...errors }
    
    if (field === 'name' && !value.trim()) {
      newErrors.name = 'Item name is required.'
    } else if (field === 'name') {
      delete newErrors.name
    }
    
    if (field === 'price') {
      const priceVal = parseInt(value)
      if (!value || isNaN(priceVal) || priceVal < 0 || priceVal !== parseFloat(value)) {
        newErrors.price = 'Please enter a valid whole number for tokens.'
      } else {
        delete newErrors.price
      }
    }
    
    if (field === 'discount_price' && formData.discounted) {
      const discountVal = parseInt(value)
      if (!value || isNaN(discountVal) || discountVal < 0 || discountVal !== parseFloat(value)) {
        newErrors.discount_price = 'Please enter a valid whole number for discount tokens.'
      } else {
        delete newErrors.discount_price
      }
    } else if (field === 'discount_price') {
      delete newErrors.discount_price
    }
    
    setErrors(newErrors)
  }

  const handleSubmit = async () => {
    // Validate
    const newErrors: Record<string, string> = {}
    if (!selectedVendorEvent) newErrors.vendor_event = 'Please select a vendor location'
    if (!formData.name.trim()) newErrors.name = 'Item name is required'
    const priceVal = parseInt(formData.price)
    if (!formData.price || isNaN(priceVal) || priceVal < 0 || priceVal !== parseFloat(formData.price)) {
      newErrors.price = 'Valid whole number is required'
    }
    if (formData.discounted) {
      const discountVal = parseInt(formData.discount_price)
      if (!formData.discount_price || isNaN(discountVal) || discountVal < 0 || discountVal !== parseFloat(formData.discount_price)) {
        newErrors.discount_price = 'Valid whole number is required when discount is enabled'
      } else if (discountVal === 0) {
        // The backend reads 0 as no discount, so saving it would quietly drop the sale.
        newErrors.discount_price = 'Discount price must be more than 0'
      } else if (!newErrors.price && discountVal >= priceVal) {
        newErrors.discount_price = 'Discount price must be less than the price'
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      if (newErrors.price || newErrors.discount_price) setActiveTab('details')
      return
    }

    try {
      await createMutation.mutateAsync({
        vendor_event: selectedVendorEvent!.id,
        name: formData.name,
        description: formData.description,
        price: parseInt(formData.price),
        discount_price: formData.discounted ? parseInt(formData.discount_price) : undefined,
        is_available: formData.is_available,
        hide_from_menu: formData.hide_from_menu,
        is_vegetarian: formData.is_vegetarian,
        is_vegan: formData.is_vegan,
        is_dairyfree: formData.is_dairy_free,
        is_glutenfree: formData.is_gluten_free,
        is_beverage: formData.is_beverage,
        is_alcoholic: formData.is_alcoholic,
        direct_pay: formData.direct_pay,
        direct_pay_only_pos: formData.direct_pay_only_pos,
        // Percentages convert percent→fraction (13 → 0.13); flat is currency.
        // Null out all overrides when direct_pay is off (they only apply to
        // direct-pay items — don't persist hidden values).
        tax_percentage: formData.direct_pay ? parseFeePctToFraction(formData.tax_percentage) : null,
        additional_fee_percentage: formData.direct_pay ? parseFeePctToFraction(formData.additional_fee_percentage) : null,
        additional_fee_fixed: formData.direct_pay ? parseFeeOverride(formData.additional_fee_fixed) : null,
        max_qty_per_order: parseInt(formData.max_qty_per_order) || 0,
        max_qty_per_user: parseInt(formData.max_qty_per_user) || 0,
      } as Partial<MenuItem>)
      onClose()
    } catch (err) {
      console.error('Error creating menu item:', err)
      const saveErrors = getMenuItemSaveErrors(err, 'Failed to create menu item')
      setErrors(saveErrors)
      if (saveErrors.price || saveErrors.discount_price) setActiveTab('details')
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-[#131313] rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col sm:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Item Preview (hidden on mobile) */}
        <div 
          className="hidden sm:flex w-80 p-6 flex-col flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}
        >
          <div className="text-center text-white mb-6">
            <h3 className="text-2xl font-bold">{formData.name || 'New Menu Item'}</h3>
            <div className="flex items-center justify-center mt-2">
              <Calendar className="w-5 h-5 mr-2" />
              <span className="text-blue-100">
                {selectedVendorEvent ? getVendorEventDisplayName(selectedVendorEvent) : 'Select Vendor'}
              </span>
            </div>
          </div>
          
          {/* Item Preview Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
            <div className="mb-3">
              <span className="text-blue-100 text-sm">Description</span>
              <p className="text-white">{formData.description || 'No description'}</p>
            </div>
            
            <div className="mb-3">
              <span className="text-blue-100 text-sm">Price</span>
              {eventType === 'direct_pay' ? (
                <p className="text-white font-semibold whitespace-nowrap">
                  ${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)}
                </p>
              ) : (
                <p className="text-white font-semibold whitespace-nowrap">
                  {formData.price || '0'} tokens{' '}
                  <span className="text-blue-200 text-sm font-normal">
                    (${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)})
                  </span>
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${formData.is_available ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm font-semibold">{formData.is_available ? 'Available' : 'Unavailable'}</span>
            </div>
          </div>
          
          {/* Action Buttons - Desktop */}
          <div className="mt-auto pt-6 space-y-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="w-full bg-white text-blue-600 font-semibold py-3 px-4 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Item'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-transparent border border-white/30 text-white font-semibold py-3 px-4 rounded-md hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Panel - Form Content */}
        <div className="flex-1 flex flex-col max-h-[95vh] sm:max-h-[90vh] min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200 dark:border-neutral-800">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 dark:text-neutral-100 truncate">{formData.name || 'New Menu Item'}</h2>
              <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                {selectedVendorEvent ? getVendorEventDisplayName(selectedVendorEvent) : 'Select a vendor location'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors ml-2 flex-shrink-0">
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-neutral-200 dark:border-neutral-800 px-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-sm ${
                  activeTab === 'details'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-2 inline">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M16 8v5a3 3 0 0 0 6 0v-5a4 4 0 1 0-8 0Z" />
                  <line x1="2" y1="9" x2="2" y2="20" />
                  <line x1="4" y1="9" x2="4" y2="20" />
                </svg>
                Item Details
              </button>
              
              <button
                onClick={() => setActiveTab('options')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-semibold text-sm ${
                  activeTab === 'options'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-2 inline">
                  <path d="M3 6h18l-2 13H5L3 6Z" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Options & Dietary
              </button>
            </nav>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto">
            {errors.submit && (
              <div className="m-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
              </div>
            )}

            {/* Item Details Tab */}
            {activeTab === 'details' && (
              <div className="p-6 space-y-6">
                {/* Vendor Location - Show search only if no default provided */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Vendor Location*
                  </label>
                  
                  {/* Only show search if no default vendor event was provided */}
                  {!defaultVendorEvent && (
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                        <input
                          type="text"
                          value={vendorSearch}
                          onChange={(e) => {
                            setVendorSearch(e.target.value)
                            setShowVendorDropdown(true)
                          }}
                          onFocus={() => setShowVendorDropdown(true)}
                          placeholder="Search vendor locations..."
                          className={`w-full px-3 py-2 pl-10 border border-neutral-200 dark:border-neutral-800 rounded-md bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                            errors.vendor_event ? 'border-red-500' : ''
                          }`}
                        />
                      </div>
                      
                      {/* Dropdown */}
                      {showVendorDropdown && filteredVendorEvents.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {filteredVendorEvents.map((ve) => {
                            const vendorName = typeof ve.vendor === 'object' ? ve.vendor.name : ''
                            const booth = ve.booth || ve.name || ''
                            return (
                              <div
                                key={ve.id}
                                onClick={() => {
                                  setSelectedVendorEvent(ve)
                                  setVendorSearch(getVendorEventDisplayName(ve))
                                  setShowVendorDropdown(false)
                                  setErrors(prev => { const newErrors = { ...prev }; delete newErrors.vendor_event; return newErrors })
                                }}
                                className="px-4 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 cursor-pointer border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                              >
                                <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                  {vendorName || 'Unknown Vendor'}
                                  {booth && booth !== vendorName && (
                                    <span className="text-neutral-500 dark:text-neutral-400"> - {booth}</span>
                                  )}
                                </div>
                                {ve.description && ve.description.length > 2 && (
                                  <div className="text-xs text-neutral-500 dark:text-neutral-400">{ve.description}</div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Selected vendor event confirmation */}
                  {selectedVendorEvent && (
                    <div className={`${defaultVendorEvent ? '' : 'mt-3'} p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md`}>
                      <div className="flex items-center">
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                        <span className="text-sm font-medium text-green-800 dark:text-green-300">Selected: </span>
                        <span className="text-sm text-green-700 dark:text-green-400 ml-1">
                          {getVendorEventDisplayName(selectedVendorEvent)}
                        </span>
                      </div>
                    </div>
                  )}
                  {errors.vendor_event && <p className="text-red-500 text-sm mt-1">{errors.vendor_event}</p>}
                </div>

                {/* Item Name */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Item Name*
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, name: e.target.value }))
                      validateField('name', e.target.value)
                    }}
                    placeholder="Enter item name"
                    className={`w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.name ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    placeholder="Item description"
                    className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  />
                </div>

                {/* Pricing Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Token Price */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      {eventType === 'direct_pay' ? 'Price*' : 'Token Price*'}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, price: e.target.value }))
                          validateField('price', e.target.value)
                        }}
                        step="1"
                        min="0"
                        placeholder="0"
                        className={`w-full px-3 py-2 pr-36 border border-neutral-200 dark:border-neutral-800 rounded-md bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                          errors.price ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {eventType === 'direct_pay' ? (
                          <span className="text-neutral-500 text-sm font-medium">
                            ${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-neutral-500 text-sm font-medium">
                            tokens <span className="text-neutral-400 ml-1">
                              (${(parseFloat(formData.price || '0') * tokenValue).toFixed(2)})
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
                  </div>

                  {/* Discount Price */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="new-item-discount-price" className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        Discount Price
                      </label>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.discounted}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              discounted: e.target.checked,
                              discount_price: e.target.checked ? prev.discount_price : '',
                            }))
                            if (!e.target.checked) {
                              setErrors(prev => {
                                const newErrors = { ...prev }
                                delete newErrors.discount_price
                                return newErrors
                              })
                            }
                          }}
                          id="new-item-discounted"
                          className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <label htmlFor="new-item-discounted" className="ml-2 text-sm text-neutral-600 dark:text-neutral-400">Enable Discount</label>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        id="new-item-discount-price"
                        type="number"
                        value={formData.discount_price}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, discount_price: e.target.value }))
                          validateField('discount_price', e.target.value)
                        }}
                        disabled={!formData.discounted}
                        step="1"
                        min="0"
                        placeholder="0"
                        className={`w-full px-3 py-2 pr-36 border border-neutral-200 dark:border-neutral-800 rounded-md text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-neutral-100 dark:disabled:bg-neutral-900 disabled:text-neutral-400 disabled:cursor-not-allowed ${
                          errors.discount_price ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {eventType === 'direct_pay' ? (
                          <span className="text-neutral-500 text-sm font-medium">
                            ${(parseFloat(formData.discount_price || '0') * tokenValue).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-neutral-500 text-sm font-medium">
                            tokens {formData.discounted && formData.discount_price && (
                              <span className="text-neutral-400 ml-1">
                                (${(parseFloat(formData.discount_price || '0') * tokenValue).toFixed(2)})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {errors.discount_price && <p className="text-red-500 text-sm mt-1">{errors.discount_price}</p>}
                  </div>
                </div>

                {/* Quantity Limits Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Max Per Order */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      Max Per Order
                    </label>
                    <input
                      type="number"
                      value={formData.max_qty_per_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_qty_per_order: e.target.value }))}
                      step="1"
                      min="0"
                      placeholder="0"
                      className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Max items per order (0 = unlimited)</p>
                  </div>

                  {/* Max Per User */}
                  <div>
                    <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                      Max Per User
                    </label>
                    <input
                      type="number"
                      value={formData.max_qty_per_user}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_qty_per_user: e.target.value }))}
                      step="1"
                      min="0"
                      placeholder="0"
                      className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-800 rounded-md bg-white dark:bg-[#131313] text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Max total per user (0 = unlimited)</p>
                  </div>
                </div>
              </div>
            )}

            {/* Options & Dietary Tab */}
            {activeTab === 'options' && (
              <div className="p-6 space-y-6">
                {/* Availability */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                    Item Availability
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_available}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Available for ordering</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.hide_from_menu}
                        onChange={(e) => setFormData(prev => ({ ...prev, hide_from_menu: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-orange-600 focus:ring-orange-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Hide from menu display</label>
                    </div>
                  </div>
                </div>

                {/* Payment Options */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                    Payment Options
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.direct_pay}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            direct_pay: e.target.checked,
                            // Turning Direct Pay off clears the dependent fields
                            // (POS-only + the fee overrides) so we never keep
                            // hidden values that would silently re-apply if it's
                            // switched back on later.
                            direct_pay_only_pos: e.target.checked ? prev.direct_pay_only_pos : false,
                            tax_percentage: e.target.checked ? prev.tax_percentage : '',
                            additional_fee_percentage: e.target.checked ? prev.additional_fee_percentage : '',
                            additional_fee_fixed: e.target.checked ? prev.additional_fee_fixed : '',
                          }))}
                          className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                        />
                        <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Direct Pay</label>
                      </div>
                      <p className="text-xs text-neutral-500 mt-1 ml-6">Allow payment without tokens</p>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.direct_pay_only_pos}
                          onChange={(e) => setFormData(prev => ({ ...prev, direct_pay_only_pos: e.target.checked }))}
                          disabled={!formData.direct_pay}
                          className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label className={`ml-2 text-sm ${formData.direct_pay ? 'text-neutral-700 dark:text-neutral-300' : 'text-neutral-400 dark:text-neutral-500'}`}>
                          POS Only
                        </label>
                      </div>
                      <p className={`text-xs mt-1 ml-6 ${formData.direct_pay ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        Restrict direct pay to point of sale
                      </p>
                    </div>
                  </div>
                  {formData.direct_pay && (
                    <DirectPayFeeInputs
                      values={{
                        tax_percentage: formData.tax_percentage,
                        additional_fee_percentage: formData.additional_fee_percentage,
                        additional_fee_fixed: formData.additional_fee_fixed,
                      }}
                      onChange={handleFeeChange}
                      eventTaxPercentage={event?.tax_percentage}
                      eventAdditionalFeePercentage={event?.additional_fee_percentage}
                      currency={event?.currency}
                    />
                  )}
                </div>

                {/* Dietary Options */}
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                    Dietary Options
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_vegetarian}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_vegetarian: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Vegetarian</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_vegan}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_vegan: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Vegan</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_dairy_free}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_dairy_free: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Dairy Free</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_gluten_free}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_gluten_free: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-green-600 focus:ring-green-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Gluten Free</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_beverage}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_beverage: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Beverage</label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_alcoholic}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_alcoholic: e.target.checked }))}
                        className="rounded border-neutral-300 dark:border-neutral-700 text-red-600 focus:ring-red-500 focus:ring-offset-0"
                      />
                      <label className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Alcoholic</label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Mobile Action Buttons */}
          <div className="sm:hidden p-4 border-t border-neutral-200 dark:border-neutral-800 space-y-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Item'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-semibold py-3 px-4 rounded-md hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
